<script>
import useSharedData from "@/use/SharedData.js";
import useValidation from "@/use/Validation.js";
import useTools from "@/use/Tools.js";
import {computed, onMounted, ref, watch} from "vue";
import {useTrainingSessionStore} from "@/stores/Training/TrainingSession.js";
import useSharedMethod from "@/use/SharedMethod.js";
import StrCatalogAutocomplete from "@/views/Training/Catalog/Components/StrCatalogAutocomplete.vue";
import StrSchoolAutocomplete from "@/views/Training/School/Components/StrSchoolAutocomplete.vue";
import axios from "axios";
import WCodeGeneration from "@/components/WCodeGeneration.vue";

export default {
    components: {WCodeGeneration, StrSchoolAutocomplete, StrCatalogAutocomplete},
    props: {
        addAction: Boolean,
        updateAction: Boolean,
        deleteAction: Boolean,
        viewAction: Boolean,
        pointingAction: Boolean,
        launchAction: Boolean,
        lockAction: Boolean,
    },
    
    setup(props) {
        const {mxRules} = useValidation();
        const {loading, dialog, overlay, inputColor, backgroundColor} = useSharedData();
        const {$route, $refs, $i18n} = useTools();
        const {getBack, getTrainingRequestPeriodName, priceFormat, formatedDate} = useSharedMethod();
        
        const trainingSessionStore = useTrainingSessionStore();
        
        const readonly = computed(() => !props.addAction && !props.updateAction);
        
        const trainingSession = ref({
            id: null,
            catalogId: null,
            dates: [],
            candidates: [],
            locked: false,
        });
        
        function findById(id) {
            loading.value = true;
            axios.get(`/trainingSessions/${id}`).then((response) => {
                trainingSession.value = response.data;
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function save() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.post('/trainingSessions', trainingSession.value).then((response) => {
                    trainingSessionStore.addTrainingSession(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function update() {
            if ($refs.form.validate()) {
                loading.value = true;
                axios.put('/trainingSessions', trainingSession.value).then((response) => {
                    trainingSessionStore.updateTrainingSession(response.data);
                    getBack();
                }).finally(() => {
                    loading.value = false;
                });
            }
        }
        
        function remove() {
            loading.value = true;
            axios.delete(`/trainingSessions/${trainingSession.value.id}`).then(() => {
                trainingSessionStore.deleteTrainingSession(trainingSession.value);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        
        function launch() {
            loading.value = true;
            axios.put(`/trainingSessions/launch/${trainingSession.value.id}`).then((response) => {
                trainingSessionStore.updateTrainingSession(response.data);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        function lock() {
            loading.value = true;
            axios.put(`/trainingSessions/lock/${trainingSession.value.id}`).then((response) => {
                trainingSessionStore.updateTrainingSession(response.data);
                getBack();
            }).finally(() => {
                loading.value = false;
            });
        }
        
        const trainingRequests = ref([]);
        
        function findTrainingRequestsByCatalog(catalogId) {
            if (catalogId && !readonly.value) {
                axios.get(`/trainingRequests/findAcceptedByCatalog`, {
                    params: {
                        catalogId: catalogId,
                        planId: trainingSession.value.planId,
                    }
                }).then(response => {
                    // trainingRequests.value = response.data;
                    const groupedRequests = response.data.reduce((acc, request) => {
                        const {
                            requestId,
                            requestPrice,
                            requestOrganizationalChartName,
                            requestPeriod,
                            requestPeriodStart,
                            requestPeriodEnd,
                            requestPeriodMonth,
                            requestPeriodQuarterlyOrHalfYearly,
                            id,
                            workerId,
                            workerName
                        } = request;
                        
                        if (!acc[requestId]) {
                            acc[requestId] = {
                                requestPrice,
                                requestId,
                                requestOrganizationalChartName,
                                requestPeriod,
                                requestPeriodStart,
                                requestPeriodEnd,
                                requestPeriodMonth,
                                requestPeriodQuarterlyOrHalfYearly,
                                requestWorkers: []
                            };
                        }
                        
                        acc[requestId].requestWorkers.push({id, workerId, workerName});
                        return acc;
                    }, {});
                    
                    trainingRequests.value = Object.values(groupedRequests);
                });
            }
        }
        
        const catalogId = computed(() => trainingSession.value.catalogId);
        watch(() => catalogId.value, () => {
            trainingRequests.value = [];
            if (catalogId.value)
                findTrainingRequestsByCatalog(catalogId.value);
        });
        
        
        function requestExist(request) {
            return trainingSession.value.candidates.filter(item1 => request.requestWorkers.some(item2 => item2.id === item1.requestWorkerId)).length > 0;
        }
        
        function addRequest(request) {
            request.requestWorkers.forEach(requestWorker => trainingSession.value.candidates.push({
                requestWorkerId: requestWorker.id,
                presences: [],
            }));
            
        }
        
        function removeRequest(request) {
            trainingSession.value.candidates = trainingSession.value.candidates.filter(item1 => !request.requestWorkers.some(item2 => item2.id === item1.requestWorkerId));
        }
        
        onMounted(() => {
            if (!props.addAction) {
                findById($route.params.id);
            }
        });
        
        const headers = [
            {
                text: $i18n.t("training.department"),
                align: "start",
                sortable: true,
                value: "requestOrganizationalChartName",
            },
            {
                text: $i18n.t("training.period"),
                align: "start",
                sortable: true,
                value: "requestPeriod",
            },
            {
                text: $i18n.t("training.workers_number"),
                align: "start",
                sortable: false,
                value: "workers",
            },
            {
                text: $i18n.t("training.price"),
                align: "start",
                sortable: true,
                value: "requestPrice",
            },
            {
                text: "...",
                sortable: false,
                align: "left",
                value: "action",
            },
        ];
        
        function rowColor(item) {
            if (requestExist(item)) {
                return 'teal lighten-4';
            } else {
                return 'red lighten-4';
            }
        }
        
        function convertPeriod(item) {
            return getTrainingRequestPeriodName({
                period: item.requestPeriod,
                periodStart: item.requestPeriodStart,
                periodEnd: item.requestPeriodEnd,
                periodMonth: item.requestPeriodMonth,
                periodQuarterlyOrHalfYearly: item.requestPeriodQuarterlyOrHalfYearly
            });
        }
        
        const setPresenceStatus = (candidate, date, newStatus) => {
            const presence = candidate.presences.find(p => p.date === date);
            if (presence) {
                presence.presenceStatus = newStatus;
            }
        };
        
        const getRawStatus = (presences, date) => {
            const entry = presences.find(p => p.date === date);
            return entry ? entry.presenceStatus : undefined;
        };
        
        const getStatusColor = (status) => {
            switch (status) {
                case 'PRESENT':
                    return 'teal';
                case 'ABSENT':
                    return 'red';
                default:
                    return 'grey'; // Pour 'NON SAISI' ou '?'
            }
        };
        
        const sortedCandidates = computed(() => {
            if (!trainingSession.value.candidates) return [];
            
            // Create a copy to avoid mutating the original data
            return [...trainingSession.value.candidates].sort((a, b) => {
                const codeA = a.requestWorkerWorkerCode || '';
                const codeB = b.requestWorkerWorkerCode || '';
                
                // localeCompare is best for strings that contain numbers (like "1472021")
                return codeA.localeCompare(codeB, undefined, {numeric: true});
            });
        });
        
        return {
            headers,
            trainingSession,
            trainingRequests,
            inputColor,
            backgroundColor,
            mxRules,
            readonly,
            loading,
            dialog,
            overlay,
            sortedCandidates,
            save,
            update,
            remove,
            launch,
            lock,
            getBack,
            convertPeriod,
            requestExist,
            addRequest,
            removeRequest,
            rowColor,
            priceFormat,
            formatedDate,
            getRawStatus,
            setPresenceStatus,
            getStatusColor,
        };
    },
};
</script>

<template>
    <v-dialog
        v-model="dialog"
        persistent
        max-width="1200"
        scrollable
    >
        <v-card>
            <w-toolbar
                v-if="pointingAction"
                color="purple"
                :title="$t('training.pointing')"
            />
            <w-dialog-toolbar
                v-else
                :add-action="addAction"
                :update-action="updateAction"
                :delete-action="deleteAction"
                :launch-action="launchAction"
                :lock-action="lockAction"
                :view-action="viewAction"
            />
            <w-overlay
                :loading="loading"
                :overlay="overlay"
                :delete-action="deleteAction"
                :launch-action="launchAction"
                :lock-action="lockAction"
                @remove="remove"
                @launch="launch"
                @lock="lock"
                @cancel="overlay = false"
            />
            
            <v-card-text>
                <v-form ref="form" lazy-validation>
                    <v-container fluid>
                        <w-fieldset :title="$t('common.details')">
                            <v-row dense>
                                <v-col cols="12">
                                    <w-label :label="$t('common.code')" required/>
                                    <w-code-generation
                                        v-model="trainingSession.code"
                                        :entity="'TRAINING_SESSION'"
                                        :load="addAction"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    ></w-code-generation>
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('training.theme')" required/>
                                    <str-catalog-autocomplete
                                        v-model="trainingSession.catalogId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="6">
                                    <w-label :label="$t('module.training_school')" required/>
                                    <str-school-autocomplete
                                        v-model="trainingSession.schoolId"
                                        :readonly="readonly"
                                        :rules="[mxRules.required]"
                                    />
                                </v-col>
                                <v-col cols="12">
                                    <w-label :label="$t('common.dates')" required/>
                                    <w-date-picker
                                        v-model="trainingSession.dates"
                                        multiple
                                        :readonly="readonly"
                                        :rules="[mxRules.listRequired]"
                                    />
                                </v-col>
                            </v-row>
                        </w-fieldset>
                        <br/>
                        <w-fieldset
                            v-if="readonly || pointingAction"
                            :title="$t('module.training_request')"
                        >
                            <v-simple-table dense>
                                <template v-slot:default>
                                    <thead>
                                    <tr>
                                        <th class="text-left">{{ $t('common.code') }}</th>
                                        <th class="text-left">{{ $t('common.name') }}</th>
                                        <th
                                            v-for="date in trainingSession.dates"
                                            :key="`th-${date}`"
                                            class="text-left"
                                        >
                                            <w-inline-text :text="formatedDate(date)"/>
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr
                                        v-for="(candidate, index) in sortedCandidates"
                                        :key="`candidate-${candidate.id || index}`"
                                    >
                                        <td class="text-left">
                                            <w-chip :text="candidate.requestWorkerWorkerCode"/>
                                        </td>
                                        <td class="text-left">
                                            <w-inline-text bold :text="candidate.requestWorkerWorkerName"/>
                                        </td>
                                        <th
                                            v-for="date in trainingSession.dates"
                                            :key="`td-${date}`"
                                            class="text-left"
                                        >
                                            <v-menu offset-y :disabled="!pointingAction">
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-chip
                                                        small
                                                        class="white--text font-weight-bold"
                                                        :color="getStatusColor(getRawStatus(candidate.presences, date))"
                                                        v-bind="attrs"
                                                        v-on="on"
                                                    >
                                                        <template
                                                            v-if="getRawStatus(candidate.presences, date) === 'PRESENT'">
                                                            {{ $t('training.presence_status_PRESENT') }}
                                                        </template>
                                                        <template
                                                            v-else-if="getRawStatus(candidate.presences, date) === 'ABSENT'">
                                                            {{ $t('training.presence_status_ABSENT') }}
                                                        </template>
                                                        <template
                                                            v-else-if="getRawStatus(candidate.presences, date) === null">
                                                            NON SAISI
                                                        </template>
                                                        <template v-else>
                                                            -
                                                        </template>
                                                    </v-chip>
                                                </template>
                                                
                                                <v-list dense>
                                                    <v-list-item @click="setPresenceStatus(candidate, date, 'PRESENT')">
                                                        <v-list-item-icon>
                                                            <v-icon color="teal">mdi-check</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>
                                                            {{ $t('training.presence_status_PRESENT') }}
                                                        </v-list-item-title>
                                                    </v-list-item>
                                                    
                                                    <v-list-item @click="setPresenceStatus(candidate, date, 'ABSENT')">
                                                        <v-list-item-icon>
                                                            <v-icon color="red">mdi-close</v-icon>
                                                        </v-list-item-icon>
                                                        <v-list-item-title>
                                                            {{ $t('training.presence_status_ABSENT') }}
                                                        </v-list-item-title>
                                                    </v-list-item>
                                                </v-list>
                                            </v-menu>
                                        </th>
                                    </tr>
                                    </tbody>
                                </template>
                            </v-simple-table>
                        </w-fieldset>
                        <w-fieldset
                            v-else
                            :title="$t('module.training_request')"
                        >
                            <w-data-table
                                :headers="headers"
                                :items="trainingRequests"
                                item-key="requestId"
                                show-expand
                                show-all-expanded
                                disable-pagination
                                :item-class="rowColor"
                            >
                                <template v-slot:[`item.requestOrganizationalChartName`]="{ item }">
                                    <w-chip color="blue darken-2" :text="item.requestOrganizationalChartName"/>
                                </template>
                                <template v-slot:[`item.requestPeriod`]="{ item }">
                                    <w-chip outlined :text="convertPeriod(item)"/>
                                </template>
                                <template v-slot:[`item.workers`]="{ item }">
                                    <w-chip color="orange" :text="item.requestWorkers.length"/>
                                </template>
                                <template v-slot:[`item.requestPrice`]="{ item }">
                                    <w-chip color="teal" outlined :text="priceFormat(item.requestPrice)"/>
                                </template>
                                <template v-slot:[`item.action`]="{ item }">
                                    <v-simple-checkbox
                                        :value="requestExist(item)"
                                        color="teal"
                                        @click="requestExist(item) ? removeRequest(item) : addRequest(item)"
                                    />
                                </template>
                                <template v-slot:expanded-item="{ headers, item }">
                                    <td :colspan="headers.length">
                                        <v-container fluid>
                                            <v-simple-table dense class="elevation-0">
                                                <template v-slot:default>
                                                    <thead>
                                                    <tr>
                                                        <th class="text-left">{{ $t('common.name') }}</th>
                                                        <th class="text-right"></th>
                                                    </tr>
                                                    </thead>
                                                    <tbody>
                                                    <tr
                                                        v-for="(w, index) in item.requestWorkers"
                                                        :key="`worker-${index}`"
                                                    >
                                                        <td class="text-left">
                                                            <strong>{{ w.workerName }}</strong>
                                                        </td>
                                                        <td class="text-right">
                                                            <v-simple-checkbox
                                                                v-if="requestExist(item)"
                                                                :value="true"
                                                                color="teal"
                                                                disabled
                                                            />
                                                        </td>
                                                    </tr>
                                                    </tbody>
                                                </template>
                                            </v-simple-table>
                                        </v-container>
                                    </td>
                                </template>
                            </w-data-table>
                        </w-fieldset>
                    </v-container>
                </v-form>
            </v-card-text>
            <v-divider/>
            <v-card-actions>
                <v-spacer/>
                <w-btn-close @press="getBack"/>
                <w-btn-add
                    :show="addAction"
                    :loading="loading"
                    @press="save"
                />
                <w-btn-update
                    :show="updateAction"
                    :loading="loading"
                    @press="update"
                />
                <w-btn-delete
                    :show="deleteAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn-launch
                    :show="launchAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn-lock
                    :show="lockAction"
                    :loading="loading"
                    @press="overlay = true"
                />
                <w-btn
                    v-if="pointingAction"
                    color="purple"
                    icon="mdi-timer-marker"
                    :text="$t('common.validate')"
                    :loading="loading"
                    @press="update"
                />
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>